import questionnaire from '../config/questionnaire';
import methodology from '../config/methodology';
import { NAVIERA_EVALUATION_CRITERIA } from '../config/questionnaire';
import { normalizeDestination } from './destinations';
import { normalizeMarketTrend } from './marketSignals';

export const getDimensionDrivers = (dimensionalIndices, questionAverages) => {
  return questionnaire.getDimensions().map((dimension) => {
    const dimensionScore = dimensionalIndices[dimension.id] ?? 0;
    const scoredQuestions = dimension.questions
      .filter((q) => questionAverages[q.id] !== undefined && questionAverages[q.id] !== null)
      .map((q) => ({
        ...q,
        value: questionAverages[q.id],
        impact: questionAverages[q.id] - dimensionScore,
      }));

    if (!scoredQuestions.length) {
      return {
        id: dimension.id,
        name: dimension.name,
        best: null,
        worst: null,
        dimensionScore,
      };
    }

    const sorted = [...scoredQuestions].sort((a, b) => b.value - a.value);
    return {
      id: dimension.id,
      name: dimension.name,
      best: sorted[0],
      worst: sorted[sorted.length - 1],
      dimensionScore,
    };
  });
};

export const aggregateShares = (items, field) => {
  const counts = items.reduce((acc, item) => {
    const rawValue = item[field];
    const key = field === 'destination'
      ? normalizeDestination(rawValue)
      : rawValue?.toString().trim() || 'Sin dato';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(counts)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);
};

export const aggregateTrendShares = (items) => {
  const counts = items.reduce((acc, item) => {
    const key = normalizeMarketTrend(item?.trend || item?.data?.d2_q5 || '');
    if (!key) return acc;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  return Object.entries(counts)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value);
};

export const buildExecutiveSummary = (globalIndex, trendStats, sentimentSummary) => {
  const interpretation = globalIndex >= 80
    ? 'El ecosistema naviero mantiene un desempeño sobresaliente.'
    : globalIndex >= 60
      ? 'El servicio es aceptable, pero existen brechas claras.'
      : globalIndex >= 40
        ? 'Se observan riesgos logísticos moderados que requieren atención ejecutiva.'
        : 'La cadena logística enfrenta una situación crítica.';

  const topTrend = trendStats[0];
  const sentimentMessage = sentimentSummary
    ? `El ${sentimentSummary.positivePct}% de los verbatims transmite percepciones positivas, mientras que un ${sentimentSummary.negativePct}% expresa alertas; lectura con ${sentimentSummary.confidenceLabel || 'muestra no clasificada'}.`
    : 'Aún no hay comentarios cualitativos suficientes para calibrar el sentimiento.';
  const trendMessage = topTrend
    ? `La variación de precios predominante es "${topTrend.label}"`
    : 'Aún no hay señal estructurada suficiente sobre variación de precios';

  return `${interpretation} ${trendMessage} y los comentarios cualitativos muestran que ${sentimentMessage}`;
};

const average = (values) => {
  const valid = values.filter((value) => typeof value === 'number' && Number.isFinite(value));
  if (!valid.length) return null;
  return valid.reduce((sum, value) => sum + value, 0) / valid.length;
};

const weightedAverage = (items) => {
  const valid = items.filter((item) => typeof item.value === 'number' && Number.isFinite(item.value) && item.weight > 0);
  if (!valid.length) return null;
  const totalWeight = valid.reduce((sum, item) => sum + item.weight, 0);
  return valid.reduce((sum, item) => sum + item.value * item.weight, 0) / totalWeight;
};

const normalizeText = (value) => (value || '').toString().trim();

const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);

const normalizeNavieraName = (detail = {}) => {
  const selected = normalizeText(detail.name);
  const other = normalizeText(detail.otherName);
  if (!selected) return '';
  if (selected === 'Otra') return other || 'Otra';
  return selected;
};

const scoreFromLikert = (value) => {
  const numeric = parseInt(value, 10);
  if (Number.isNaN(numeric)) return null;
  return methodology.normalizeLikert(numeric);
};

const volumeWeight = (value = '') => {
  if (value.includes('+60')) return 1.5;
  if (value.includes('30%-60')) return 1;
  if (value.includes('-30')) return 0.65;
  return 1;
};

const severityRisk = {
  'Sin impacto relevante': 0,
  Bajo: 25,
  Medio: 50,
  Alto: 75,
  Crítico: 95,
};

const resolutionRisk = {
  Sí: 0,
  Parcialmente: 45,
  No: 80,
  'No aplica': 0,
};

const calculateDetailMetrics = (detail = {}) => {
  const scores = detail.scores || {};
  const criterionScores = NAVIERA_EVALUATION_CRITERIA.reduce((acc, criterion) => {
    acc[criterion.id] = scoreFromLikert(scores[criterion.id]);
    return acc;
  }, {});

  const availability = criterionScores.availability;
  const quality = average([
    criterionScores.communication,
    criterionScores.resolution,
    criterionScores.documentation,
    criterionScores.overall,
  ]);
  const agility = average([
    criterionScores.punctuality,
    criterionScores.communication,
    criterionScores.resolution,
  ]);
  const costs = criterionScores.costTransparency;
  const overallScore = average(Object.values(criterionScores));
  const problemList = Array.isArray(detail.problems) ? detail.problems.filter(Boolean) : [];
  const materialProblems = problemList.filter((problem) => problem !== 'Sin problemas relevantes');

  return {
    availability,
    quality,
    agility,
    costs,
    overallScore,
    problemList,
    materialProblems,
    severityScore: severityRisk[detail.severity] ?? null,
    resolutionScore: resolutionRisk[detail.resolved] ?? null,
  };
};

const buildLegacyDiagnostic = (submission = {}) => {
  const data = submission.data || {};
  const naviera = submission.navieraPrincipal || data.meta_naviera_principal;
  if (!naviera) return [];
  return [{
    name: naviera,
    scores: {
      availability: data.d1_q3 || data.d1_q1,
      punctuality: data.d3_q1,
      communication: data.d3_q5 || data.d3_q2,
      resolution: data.d3_q3,
      costTransparency: data.d2_q1 || data.d3_q6,
      documentation: data.d3_q1,
      overall: data.d3_q4,
    },
    problems: [],
    severity: '',
    resolved: '',
    volumeShare: 'Principal (+60% del volumen)',
    comment: data.meta_naviera_comentario || '',
    legacy: true,
  }];
};

export const getSubmissionNavieraDiagnostics = (submission = {}) => {
  const explicitDetails = submission.data?.meta_naviera_detalle;
  if (Array.isArray(explicitDetails) && explicitDetails.length) {
    return explicitDetails
      .map((detail) => ({ ...detail, name: normalizeNavieraName(detail) }))
      .filter((detail) => detail.name);
  }
  return buildLegacyDiagnostic(submission);
};

const getSegmentLabel = (submission = {}, field = '') => {
  if (field === 'destination') return normalizeDestination(submission.destination);
  if (field === 'port') return normalizeText(submission.port) || 'Otro puerto';
  if (field === 'exportType') return normalizeText(submission.exportType) || 'Otros';
  return normalizeText(submission[field]) || 'Sin dato';
};

const getConfidenceLabel = (count) => {
  if (count >= 8) return 'base sólida';
  if (count >= 3) return 'señal direccional';
  return 'muestra inicial';
};

const normalizeProblemLabel = (detail = {}, problem = '') => {
  const value = normalizeText(problem);
  if (!value || value === 'Sin problemas relevantes') return '';
  if (value === 'Otro') return normalizeText(detail.otherProblem) || 'Otro problema';
  return value;
};

const countRows = (counts = {}, total = 0, limit = 5) => (
  Object.entries(counts)
    .map(([label, value]) => ({
      label,
      value,
      percent: total ? Math.round((value / total) * 100) : 0,
    }))
    .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label))
    .slice(0, limit)
);

const addCount = (target, key, amount = 1) => {
  const label = normalizeText(key);
  if (!label) return;
  target[label] = (target[label] || 0) + amount;
};

const incidentUnits = (value = '') => {
  if (value === '1') return 1;
  if (value === '2-3') return 2.5;
  if (value === '4+') return 4;
  return 0;
};

const normalizeRouteLabel = (detail = {}, submission = {}) => {
  const route = normalizeText(detail.route);
  if (route) return normalizeDestination(route);
  return normalizeDestination(submission.destination);
};

const addOperationalDetailToGroup = (group, detail = {}, submission = {}) => {
  const problems = Array.isArray(detail.problems)
    ? detail.problems.map((problem) => normalizeProblemLabel(detail, problem)).filter(Boolean)
    : [];
  const incidents = incidentUnits(detail.incidentCount);
  const isSevere = detail.severity === 'Alto' || detail.severity === 'Crítico';
  const isUnresolved = detail.resolved === 'No' || detail.resolved === 'Parcialmente';

  group.evaluationCount += 1;
  group.incidentUnits += incidents;
  if (isSevere) group.severeCount += 1;
  if (isUnresolved) group.unresolvedCount += 1;

  problems.forEach((problem) => addCount(group.problemCounts, problem));
  addCount(group.routeCounts, normalizeRouteLabel(detail, submission));
};

const buildSegmentRow = ([label, group], totalParticipants) => {
  const globalScore = average(group.globalScores) ?? 0;
  const dimensionScores = questionnaire.getDimensions()
    .map((dimension) => ({
      id: dimension.id,
      name: dimension.name,
      score: average(group.dimensionScores[dimension.id] || []),
    }))
    .filter((item) => isFiniteNumber(item.score));
  const weakestDimension = dimensionScores
    .slice()
    .sort((a, b) => a.score - b.score)[0] || null;
  const topProblem = countRows(group.problemCounts, group.evaluationCount, 1)[0] || null;
  const topRoute = countRows(group.routeCounts, group.evaluationCount, 1)[0] || null;

  return {
    label,
    count: group.count,
    percent: totalParticipants ? Math.round((group.count / totalParticipants) * 100) : 0,
    score: Number(globalScore.toFixed(1)),
    pressure: Number((100 - globalScore).toFixed(1)),
    weakestDimension,
    topProblem,
    topRoute,
    incidentUnits: Number(group.incidentUnits.toFixed(1)),
    severeCount: group.severeCount,
    unresolvedCount: group.unresolvedCount,
    confidenceLabel: getConfidenceLabel(group.count),
  };
};

export const buildSegmentPerformance = (submissions = [], field = 'exportType') => {
  const groups = submissions.reduce((acc, submission) => {
    const label = getSegmentLabel(submission, field);
    if (!label || label === 'Sin dato') return acc;

    const result = methodology.calculateIndices(submission.data || {});
    if (!isFiniteNumber(result.globalIndex)) return acc;

    if (!acc[label]) {
      acc[label] = {
        count: 0,
        globalScores: [],
        dimensionScores: questionnaire.getDimensions().reduce((dimAcc, dimension) => {
          dimAcc[dimension.id] = [];
          return dimAcc;
        }, {}),
        evaluationCount: 0,
        incidentUnits: 0,
        severeCount: 0,
        unresolvedCount: 0,
        problemCounts: {},
        routeCounts: {},
      };
    }

    const group = acc[label];
    group.count += 1;
    group.globalScores.push(result.globalIndex);

    Object.entries(result.dimensionalIndices || {}).forEach(([dimensionId, value]) => {
      if (isFiniteNumber(value) && group.dimensionScores[dimensionId]) {
        group.dimensionScores[dimensionId].push(value);
      }
    });

    getSubmissionNavieraDiagnostics(submission).forEach((detail) => addOperationalDetailToGroup(group, detail, submission));
    return acc;
  }, {});

  return Object.entries(groups)
    .map((entry) => buildSegmentRow(entry, submissions.length))
    .sort((a, b) => b.pressure - a.pressure || b.count - a.count || a.label.localeCompare(b.label));
};

const createOperationalCarrierGroup = (name) => ({
  name,
  evaluationCount: 0,
  incidentUnits: 0,
  severeCount: 0,
  unresolvedCount: 0,
  problemCounts: {},
  routeCounts: {},
});

export const buildOperationalFriction = (submissions = []) => {
  const totals = {
    evaluationCount: 0,
    incidentUnits: 0,
    severeCount: 0,
    unresolvedCount: 0,
    problemCounts: {},
    severityCounts: {},
    impactDaysCounts: {},
    costImpactCounts: {},
    resolutionCounts: {},
    routeCounts: {},
    carrierGroups: {},
  };

  submissions.forEach((submission) => {
    getSubmissionNavieraDiagnostics(submission).forEach((detail) => {
      const carrier = normalizeNavieraName(detail);
      const incidents = incidentUnits(detail.incidentCount);
      const problems = Array.isArray(detail.problems)
        ? detail.problems.map((problem) => normalizeProblemLabel(detail, problem)).filter(Boolean)
        : [];
      const route = normalizeRouteLabel(detail, submission);
      const isSevere = detail.severity === 'Alto' || detail.severity === 'Crítico';
      const isUnresolved = detail.resolved === 'No' || detail.resolved === 'Parcialmente';

      totals.evaluationCount += 1;
      totals.incidentUnits += incidents;
      if (isSevere) totals.severeCount += 1;
      if (isUnresolved) totals.unresolvedCount += 1;

      addCount(totals.severityCounts, detail.severity || 'Sin dato');
      addCount(totals.impactDaysCounts, detail.impactDays || 'Sin dato');
      addCount(totals.costImpactCounts, detail.costImpact || 'Sin dato');
      addCount(totals.resolutionCounts, detail.resolved || 'Sin dato');
      addCount(totals.routeCounts, route);
      problems.forEach((problem) => addCount(totals.problemCounts, problem));

      if (carrier) {
        if (!totals.carrierGroups[carrier]) totals.carrierGroups[carrier] = createOperationalCarrierGroup(carrier);
        addOperationalDetailToGroup(totals.carrierGroups[carrier], detail, submission);
      }
    });
  });

  const carrierRows = Object.values(totals.carrierGroups)
    .map((group) => {
      const severeRate = group.evaluationCount ? (group.severeCount / group.evaluationCount) * 100 : 0;
      const unresolvedRate = group.evaluationCount ? (group.unresolvedCount / group.evaluationCount) * 100 : 0;
      const incidentPressure = Math.min(100, (group.incidentUnits / Math.max(1, group.evaluationCount * 4)) * 100);
      const risk = average([severeRate, unresolvedRate, incidentPressure]) ?? 0;
      return {
        name: group.name,
        evaluationCount: group.evaluationCount,
        incidentUnits: Number(group.incidentUnits.toFixed(1)),
        severeCount: group.severeCount,
        unresolvedCount: group.unresolvedCount,
        risk: Number(risk.toFixed(1)),
        topProblems: countRows(group.problemCounts, group.evaluationCount, 3),
        topRoute: countRows(group.routeCounts, group.evaluationCount, 1)[0] || null,
      };
    })
    .sort((a, b) => b.risk - a.risk || b.evaluationCount - a.evaluationCount || a.name.localeCompare(b.name))
    .slice(0, 5);

  return {
    evaluationCount: totals.evaluationCount,
    incidentUnits: Number(totals.incidentUnits.toFixed(1)),
    severeCount: totals.severeCount,
    unresolvedCount: totals.unresolvedCount,
    severeShare: totals.evaluationCount ? Math.round((totals.severeCount / totals.evaluationCount) * 100) : 0,
    unresolvedShare: totals.evaluationCount ? Math.round((totals.unresolvedCount / totals.evaluationCount) * 100) : 0,
    problemRows: countRows(totals.problemCounts, totals.evaluationCount, 6),
    severityRows: countRows(totals.severityCounts, totals.evaluationCount, 5),
    impactDaysRows: countRows(totals.impactDaysCounts, totals.evaluationCount, 5),
    costImpactRows: countRows(totals.costImpactCounts, totals.evaluationCount, 5),
    resolutionRows: countRows(totals.resolutionCounts, totals.evaluationCount, 4),
    routeRows: countRows(totals.routeCounts, totals.evaluationCount, 5),
    carrierRows,
  };
};

export const buildNavieraPerformance = (submissions = []) => {
  const grouped = submissions.reduce((acc, submission) => {
    const diagnostics = getSubmissionNavieraDiagnostics(submission);
    diagnostics.forEach((detail) => {
      const naviera = detail.name;
      if (!naviera) return;
      if (!acc[naviera]) {
        acc[naviera] = {
          name: naviera,
          details: [],
          submissionIds: new Set(),
          comments: [],
          problemCounts: {},
        };
      }
      const weight = volumeWeight(detail.volumeShare);
      const metrics = calculateDetailMetrics(detail);
      acc[naviera].details.push({ detail, metrics, weight });
      acc[naviera].submissionIds.add(submission.docId || submission.companyId || `${naviera}_${acc[naviera].details.length}`);

      const comment = normalizeText(detail.comment);
      if (comment) acc[naviera].comments.push(comment);

      metrics.materialProblems.forEach((problem) => {
        acc[naviera].problemCounts[problem] = (acc[naviera].problemCounts[problem] || 0) + 1;
      });
    });
    return acc;
  }, {});

  return Object.entries(grouped)
    .map(([name, group]) => {
      const quality = weightedAverage(group.details.map(({ metrics, weight }) => ({ value: metrics.quality, weight })));
      const agility = weightedAverage(group.details.map(({ metrics, weight }) => ({ value: metrics.agility, weight })));
      const costs = weightedAverage(group.details.map(({ metrics, weight }) => ({ value: metrics.costs, weight })));
      const availability = weightedAverage(group.details.map(({ metrics, weight }) => ({ value: metrics.availability, weight })));
      const overallScore = weightedAverage(group.details.map(({ metrics, weight }) => ({ value: metrics.overallScore, weight })));
      const averageSeverity = average(group.details.map(({ metrics }) => metrics.severityScore));
      const averageResolutionRisk = average(group.details.map(({ metrics }) => metrics.resolutionScore));
      const materialProblemDetails = group.details.filter(({ metrics }) => metrics.materialProblems.length > 0).length;
      const problemIncidence = group.details.length ? (materialProblemDetails / group.details.length) * 100 : 0;
      const scoreRisk = overallScore === null ? average([quality, agility, costs, availability].map((value) => (value === null ? null : 100 - value))) : 100 - overallScore;
      const complaintRisk = average([
        scoreRisk,
        averageSeverity,
        averageResolutionRisk,
        problemIncidence,
      ]);
      const topProblems = Object.entries(group.problemCounts)
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label))
        .slice(0, 4);
      const unresolvedCount = group.details.filter(({ detail }) => detail.resolved === 'No' || detail.resolved === 'Parcialmente').length;
      const sampleSize = group.submissionIds.size;
      const confidenceLabel = sampleSize >= 5 ? 'base sólida' : sampleSize >= 2 ? 'señal direccional' : 'muestra inicial';

      return {
        name,
        quality: quality ?? 0,
        agility: agility ?? 0,
        costs: costs ?? 0,
        availability: availability ?? 0,
        overallScore: overallScore ?? average([quality, agility, costs, availability]) ?? 0,
        complaintRisk: complaintRisk ?? 0,
        sampleSize,
        evaluationCount: group.details.length,
        confidenceLabel,
        topProblems,
        unresolvedCount,
        comments: Array.from(new Set(group.comments)).slice(0, 4),
        highlight: `${sampleSize} empresa${sampleSize === 1 ? '' : 's'} · ${group.details.length} evaluación${group.details.length === 1 ? '' : 'es'} (${confidenceLabel}).`,
      };
    })
    .sort((a, b) => b.sampleSize - a.sampleSize || b.overallScore - a.overallScore || a.name.localeCompare(b.name));
};
